import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableRow,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Print as PrintIcon,
  Close as CloseIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { api } from '../config/api';

interface LocationContainer {
  container_no: string;
  pallet_count: number;
  qty: number;
  delivery_day: string | null;
}

interface Location {
  location_name: string;
  total_pallet_count: number;
  total_qty: number;
  measurement_unit: string | null;
  is_pending?: boolean;  // True for TBA entries (unreceived stock)
  containers: LocationContainer[];  // Containers that contributed to this location
}

interface Container {
  container_id: number;
  container_no: string;
  seal_no: string | null;
  delivery_day: string | null;
  status: string;
  pallet_count: number;
  packing_list_qty: number;
}

interface OrderLine {
  line_number: number;
  stock_code: string;
  description: string;
  ordered_qty: number;
  unit: string;
  is_reel: boolean;
  expected_delivery: string | null;
  chain_of_custody: string | null;
  locations: Location[];  // Physical receipt - aggregated by location
  containers: Container[];  // Packing list - by container
  total_packing_list_qty: number;
  total_pallet_count: number;
  total_receipt_qty: number;
  total_receipt_pallets: number;
}

interface ReportData {
  po_number: string;
  supplier_code: string;
  expected_delivery_by: string | null;
  due_diligence_completed: boolean;
  order_lines: OrderLine[];
  summary: {
    total_lines: number;
    total_ordered_qty: number;
    total_received_qty: number;
  };
}

interface POReceiptingReportProps {
  open: boolean;
  onClose: () => void;
  initialPONumber?: string;
}

const POReceiptingReport: React.FC<POReceiptingReportProps> = ({
  open,
  onClose,
  initialPONumber = ''
}) => {
  const [poNumber, setPONumber] = useState(initialPONumber);
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && initialPONumber) {
      setPONumber(initialPONumber);
      loadReport(initialPONumber);
    }
  }, [open, initialPONumber]);

  const loadReport = async (po: string) => {
    if (!po.trim()) {
      setError('Please enter a PO number');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/po-receipting-report/${po.trim()}`);
      if (response.data.success) {
        setReportData(response.data);
      } else {
        setError(response.data.error || 'Failed to load report');
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load report';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadReport(poNumber);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatQuantity = (qty: number, isReel: boolean) => {
    if (isReel) {
      return `${qty.toLocaleString()} kg`;
    }
    return `${qty.toLocaleString()} sheets`;
  };

  // Calculate the total number of rows needed for a line (for rowspan calculations)
  // New structure: Order -> Locations -> Containers
  const getLineRowCount = (line: OrderLine): number => {
    if (line.locations.length === 0) return 1;
    // Each location shows its contributing containers
    return line.locations.reduce((sum, location) => {
      return sum + Math.max(location.containers.length, 1);
    }, 0);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '80vh',
          '@media print': {
            maxWidth: '100%',
            margin: 0,
            boxShadow: 'none'
          }
        }
      }}
    >
      <DialogTitle sx={{ '@media print': { display: 'none' } }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h6" component="span">
            PO Receipting Report
          </Typography>
          <TextField
            size="small"
            placeholder="Enter PO Number"
            value={poNumber}
            onChange={(e) => setPONumber(e.target.value)}
            onKeyPress={handleKeyPress}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleSearch} size="small">
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{ width: 200 }}
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {reportData && !loading && (
          <Box sx={{ '@media print': { padding: 0 } }}>
            {/* Report Header */}
            <Box
              sx={{
                border: '2px solid #000',
                mb: 2,
                '@media print': { pageBreakInside: 'avoid' }
              }}
            >
              {/* Title Bar */}
              <Box sx={{ backgroundColor: '#1976d2', color: 'white', p: 1 }}>
                <Typography variant="h6" align="center">
                  PO Receipting Report - {reportData.po_number}
                </Typography>
              </Box>

              {/* Header Info Grid */}
              <Box sx={{ display: 'flex' }}>
                <Box sx={{ flex: 1, p: 1, borderRight: '1px solid #000' }}>
                  <Typography variant="caption" display="block">
                    <strong>Supplier:</strong> {reportData.supplier_code}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, p: 1, borderRight: '1px solid #000' }}>
                  <Typography variant="caption" display="block">
                    <strong>Total Lines:</strong> {reportData.summary.total_lines}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, p: 1 }}>
                  <Typography variant="caption" display="block">
                    <strong>Due Diligence:</strong>{' '}
                    <span style={{ color: reportData.due_diligence_completed ? 'green' : 'red' }}>
                      {reportData.due_diligence_completed ? 'Completed' : 'Pending'}
                    </span>
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Main Report Table */}
            <Table size="small" sx={{ '& td, & th': { border: '1px solid #000', padding: '4px 8px' } }}>
              <TableBody>
                {/* Section Headers */}
                <TableRow sx={{ backgroundColor: '#d0d0d0' }}>
                  <TableCell colSpan={3} sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                    Order
                  </TableCell>
                  <TableCell colSpan={3} sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                    Physical Receipt
                  </TableCell>
                  <TableCell colSpan={4} sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                    Packing List
                  </TableCell>
                </TableRow>

                {/* Column Headers */}
                <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                  <TableCell sx={{ fontWeight: 'bold', width: '5%' }}>Line</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '14%' }}>Stock Code</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '10%' }} align="right">Ordered</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '8%' }}>Location</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '6%' }} align="center">Pallets</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '10%' }} align="right">Qty</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '12%' }}>Container</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '6%' }} align="center">Pallets</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '10%' }} align="right">Qty</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '10%' }}>Date</TableCell>
                </TableRow>

                {/* Data Rows - Order -> Locations -> Containers */}
                {reportData.order_lines.map((line) => {
                  const lineRowCount = getLineRowCount(line);
                  const rows: React.ReactNode[] = [];
                  let isFirstLineRow = true;

                  if (line.locations.length === 0) {
                    // No locations - single row with dashes
                    rows.push(
                      <TableRow key={`line-${line.line_number}`}>
                        <TableCell>{line.line_number}</TableCell>
                        <TableCell>{line.stock_code}</TableCell>
                        <TableCell align="right">
                          {formatQuantity(line.ordered_qty, line.is_reel)}
                        </TableCell>
                        <TableCell>-</TableCell>
                        <TableCell align="center">-</TableCell>
                        <TableCell align="right">-</TableCell>
                        <TableCell>-</TableCell>
                        <TableCell align="center">-</TableCell>
                        <TableCell align="right">-</TableCell>
                        <TableCell>-</TableCell>
                      </TableRow>
                    );
                  } else {
                    // Has locations - render Order -> Locations -> Containers
                    line.locations.forEach((location, locationIdx) => {
                      const locationRowCount = Math.max(location.containers.length, 1);
                      let isFirstLocationRow = true;

                      // Style for pending/TBA locations (red font)
                      const pendingStyle = location.is_pending ? { color: '#d32f2f' } : {};

                      if (location.containers.length === 0) {
                        // Location with no container breakdown
                        rows.push(
                          <TableRow key={`line-${line.line_number}-loc-${locationIdx}`}>
                            {isFirstLineRow && (
                              <>
                                <TableCell rowSpan={lineRowCount}>{line.line_number}</TableCell>
                                <TableCell rowSpan={lineRowCount}>{line.stock_code}</TableCell>
                                <TableCell rowSpan={lineRowCount} align="right">
                                  {formatQuantity(line.ordered_qty, line.is_reel)}
                                </TableCell>
                              </>
                            )}
                            <TableCell rowSpan={locationRowCount} sx={pendingStyle}>
                              {location.location_name}
                            </TableCell>
                            <TableCell rowSpan={locationRowCount} align="center" sx={pendingStyle}>
                              {location.total_pallet_count}
                            </TableCell>
                            <TableCell rowSpan={locationRowCount} align="right" sx={pendingStyle}>
                              {location.total_qty.toLocaleString()} {location.measurement_unit || ''}
                            </TableCell>
                            <TableCell sx={pendingStyle}>-</TableCell>
                            <TableCell align="center" sx={pendingStyle}>-</TableCell>
                            <TableCell align="right" sx={pendingStyle}>-</TableCell>
                            <TableCell sx={pendingStyle}>-</TableCell>
                          </TableRow>
                        );
                        isFirstLineRow = false;
                      } else {
                        // Location with container breakdown
                        location.containers.forEach((container, containerIdx) => {
                          rows.push(
                            <TableRow
                              key={`line-${line.line_number}-loc-${locationIdx}-cont-${containerIdx}`}
                            >
                              {isFirstLineRow && (
                                <>
                                  <TableCell rowSpan={lineRowCount}>{line.line_number}</TableCell>
                                  <TableCell rowSpan={lineRowCount}>{line.stock_code}</TableCell>
                                  <TableCell rowSpan={lineRowCount} align="right">
                                    {formatQuantity(line.ordered_qty, line.is_reel)}
                                  </TableCell>
                                </>
                              )}
                              {isFirstLocationRow && (
                                <>
                                  <TableCell rowSpan={locationRowCount} sx={pendingStyle}>
                                    {location.location_name}
                                  </TableCell>
                                  <TableCell rowSpan={locationRowCount} align="center" sx={pendingStyle}>
                                    {location.total_pallet_count}
                                  </TableCell>
                                  <TableCell rowSpan={locationRowCount} align="right" sx={pendingStyle}>
                                    {location.total_qty.toLocaleString()} {location.measurement_unit || ''}
                                  </TableCell>
                                </>
                              )}
                              <TableCell sx={pendingStyle}>{container.container_no}</TableCell>
                              <TableCell align="center" sx={pendingStyle}>{container.pallet_count}</TableCell>
                              <TableCell align="right" sx={pendingStyle}>
                                {container.qty.toLocaleString()} {location.measurement_unit || ''}
                              </TableCell>
                              <TableCell sx={pendingStyle}>{formatDate(container.delivery_day)}</TableCell>
                            </TableRow>
                          );
                          isFirstLineRow = false;
                          isFirstLocationRow = false;
                        });
                      }
                    });
                  }

                  return rows;
                })}
              </TableBody>
            </Table>
          </Box>
        )}

        {!reportData && !loading && !error && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <Typography color="textSecondary">
              Enter a PO number to generate the report
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ '@media print': { display: 'none' } }}>
        <Button onClick={onClose} startIcon={<CloseIcon />}>
          Close
        </Button>
        <Button
          onClick={handlePrint}
          variant="contained"
          startIcon={<PrintIcon />}
          disabled={!reportData}
        >
          Print
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default POReceiptingReport;
